/**
 * Points are decimal strings, and this is the only module allowed to treat
 * them as anything else.
 *
 * The 1954 British Grand Prix split one point between seven drivers. The API
 * sends `"0.14"`; `parseFloat` would give `0.14000000000000001` and
 * `(0.14 * 7).toFixed(2)` gives `0.98`, not `1.00`. So no arithmetic happens
 * here and none happens anywhere else in the frontend: every total the
 * interface shows was computed on the server in exact fractions.
 *
 * Deliberately absent, and not an oversight: `addPoints` and `sumPoints`. If a
 * view needs a total, the API has to provide it. `scripts/check-points-discipline.mjs`
 * fails the build if `parseFloat`, `Number(` or `.toFixed(` appears outside
 * this file and the chart's geometry.
 */

/** A decimal string from the API. The brand stops a raw string slipping in. */
export type Points = string & { readonly __brand: 'Points' }

const DECIMAL = /^-?\d+(\.\d+)?$/

/** Tag an API value as points, or null if it is not a decimal string. */
export function asPoints(raw: unknown): Points | null {
  return typeof raw === 'string' && DECIMAL.test(raw) ? (raw as Points) : null
}

/**
 * Trim a points value for display, by string surgery only.
 *
 * The official tables are stored to four decimals and the engine quantises to
 * two, so the same driver can arrive as `"90.0000"` or `"90.00"`. Both should
 * read `90`, and `"25.1400"` should read `25.14` -- the cent that a shared
 * fastest lap actually paid, with nothing invented and nothing rounded.
 */
export function formatPoints(raw: unknown, fallback = '—'): string {
  const value = asPoints(raw)
  if (value === null) return fallback
  if (!value.includes('.')) return value
  const trimmed = value.replace(/0+$/, '').replace(/\.$/, '')
  return trimmed === '' || trimmed === '-' ? '0' : trimmed
}

/** True when a points value is zero, however many zeros it is written with. */
export function isZeroPoints(raw: unknown): boolean {
  const value = asPoints(raw)
  return value !== null && /^-?0*(\.0*)?$/.test(value)
}

/**
 * Order two points values, without going through a float.
 *
 * Sorting is a display need, so it must not borrow the one conversion below:
 * `"9"` must sort above `"10"` numerically, and `"0.14"` above `"0.1400"` is
 * a tie. Compared digit by digit, which is exact at any length.
 */
export function comparePoints(a: unknown, b: unknown): number {
  const left = asPoints(a)
  const right = asPoints(b)
  if (left === null || right === null) return left === right ? 0 : left === null ? -1 : 1

  const negative = left.startsWith('-')
  if (negative !== right.startsWith('-')) return negative ? -1 : 1
  const sign = negative ? -1 : 1
  const [leftWhole = '', leftPart = ''] = left.replace('-', '').split('.')
  const [rightWhole = '', rightPart = ''] = right.replace('-', '').split('.')

  const width = Math.max(leftWhole.length, rightWhole.length)
  const whole = order(leftWhole.padStart(width, '0'), rightWhole.padStart(width, '0'))
  if (whole !== 0) return sign * whole

  const depth = Math.max(leftPart.length, rightPart.length)
  const part = order(leftPart.padEnd(depth, '0'), rightPart.padEnd(depth, '0'))
  // Not `sign * part`: that returns -0 for two equal negatives, which compares
  // equal but is not the same value, and a test that checks for 0 should pass.
  return part === 0 ? 0 : sign * part
}

/** Plain comparison of equal-length digit strings: no locale, no collation. */
function order(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0
}

/**
 * The one conversion boundary in the application.
 *
 * A chart axis is pixels, and pixels are numbers, so geometry has to convert
 * somewhere. It happens here, named loudly, and `chart/chartGeometry.ts` is
 * the only other place allowed to call it. Never use it to compute a total
 * that is then shown to a reader: show the string the server sent.
 */
export function pointsToNumber(raw: unknown): number {
  const value = asPoints(raw)
  if (value === null) return 0
  return Number(value)
}
